/** @format */

import React from "react";
import { Box, Tooltip, Fade, Collapse } from "@mui/material";
import ThumbUpAltRoundedIcon from "@mui/icons-material/ThumbUpAltRounded";
import ThumbUpOffAltRoundedIcon from "@mui/icons-material/ThumbUpOffAltRounded";
import ThumbDownAltRoundedIcon from "@mui/icons-material/ThumbDownAltRounded";
import ThumbDownOffAltRoundedIcon from "@mui/icons-material/ThumbDownOffAltRounded";

// Quick structured reasons offered after a downvote. The machine-readable
// `value` travels in the feedback `comment` field ("reason:wrong_numbers"),
// turning useless binary downvotes into actionable categories.
const DOWNVOTE_REASONS = [
  { value: "reason:wrong_numbers", label: "Wrong numbers" },
  { value: "reason:wrong_time_range", label: "Wrong time range" },
  { value: "reason:misunderstood_question", label: "Misunderstood my question" },
  { value: "reason:missing_data", label: "Missing data" },
];

// Small "Was this answer correct?" affordance shown under every ProPilot
// answer. The rating is lifted to useChatBot (optimistic) and persisted to
// /api/feedback so we can analyse answer quality later. A downvote expands
// quick reason chips; an upvote stays one tap.
const VoteButton = ({ active, activeColor, activeBg, onClick, children, label }) => (
  <Tooltip title={label} arrow placement="top">
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        // Bigger touch target on phones (36px ≈ minimum comfortable tap).
        width: { xs: 36, md: 30 },
        height: { xs: 36, md: 30 },
        p: 0,
        cursor: "pointer",
        borderRadius: "8px",
        border: "1px solid",
        borderColor: active ? activeColor : "#e2e8f0",
        backgroundColor: active ? activeBg : "#fff",
        color: active ? activeColor : "#94a3b8",
        transition: "all 140ms ease",
        "&:hover": {
          borderColor: activeColor,
          color: activeColor,
          backgroundColor: activeBg,
          transform: "translateY(-1px)",
        },
        "& svg": { fontSize: 16 },
      }}
    >
      {children}
    </Box>
  </Tooltip>
);

const ReasonChip = ({ label, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      px: 1.25,
      py: 0.5,
      cursor: "pointer",
      borderRadius: "999px",
      border: "1px solid #fecaca",
      backgroundColor: "#fff",
      color: "#b91c1c",
      fontSize: 11.5,
      fontWeight: 600,
      whiteSpace: "nowrap",
      transition: "all 140ms ease",
      "&:hover": { backgroundColor: "#fef2f2", borderColor: "#fca5a5" },
    }}
  >
    {label}
  </Box>
);

const FeedbackBar = ({ feedback, hasError, onVote }) => {
  // Once a reason has been sent the chips collapse into the thank-you line.
  const [reasonSent, setReasonSent] = React.useState(false);
  const [otherOpen, setOtherOpen] = React.useState(false);
  const [otherText, setOtherText] = React.useState("");

  // Vote cleared or flipped to 👍 — reset the reason flow for a fresh pass.
  React.useEffect(() => {
    if (feedback !== "down") {
      setReasonSent(false);
      setOtherOpen(false);
      setOtherText("");
    }
  }, [feedback]);

  const voted = feedback === "up" || feedback === "down";
  const askingReason = feedback === "down" && !reasonSent && !hasError;

  const sendReason = (comment) => {
    setReasonSent(true);
    setOtherOpen(false);
    // Re-affirms the existing downvote with the reason attached.
    onVote("down", comment);
  };

  const handleOtherSubmit = () => {
    const text = otherText.trim();
    if (!text) return;
    sendReason(text);
  };

  return (
    <Box sx={{ mt: 1.5, pt: 1.25, borderTop: "1px dashed #e2e8f0" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Box
          component="span"
          sx={{ fontSize: 11.5, fontWeight: 600, color: "#94a3b8", mr: 0.25 }}
        >
          Was this answer correct?
        </Box>
        <VoteButton
          active={feedback === "up"}
          activeColor="#059669"
          activeBg="#ecfdf5"
          onClick={() => onVote("up")}
          label="Yes, this is correct"
        >
          {feedback === "up" ? (
            <ThumbUpAltRoundedIcon />
          ) : (
            <ThumbUpOffAltRoundedIcon />
          )}
        </VoteButton>
        <VoteButton
          active={feedback === "down"}
          activeColor="#dc2626"
          activeBg="#fef2f2"
          onClick={() => onVote("down")}
          label="No, this is wrong"
        >
          {feedback === "down" ? (
            <ThumbDownAltRoundedIcon />
          ) : (
            <ThumbDownOffAltRoundedIcon />
          )}
        </VoteButton>

        <Fade in={(voted && !askingReason) || hasError}>
          <Box
            component="span"
            sx={{
              fontSize: 11.5,
              fontWeight: 600,
              ml: 0.5,
              color: hasError ? "#dc2626" : "#059669",
            }}
          >
            {hasError
              ? "Couldn't save — tap to retry"
              : feedback === "down"
              ? "Thanks — we'll use this to improve."
              : "Thanks for the feedback!"}
          </Box>
        </Fade>
      </Box>

      {/* Downvote → quick structured reasons (one tap each) or free text. */}
      <Collapse in={askingReason} unmountOnExit>
        <Box sx={{ mt: 1.25 }}>
          <Box
            sx={{ fontSize: 11.5, fontWeight: 600, color: "#64748b", mb: 0.75 }}
          >
            What went wrong?
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {DOWNVOTE_REASONS.map((r) => (
              <ReasonChip
                key={r.value}
                label={r.label}
                onClick={() => sendReason(r.value)}
              />
            ))}
            {!otherOpen && (
              <ReasonChip label="Other…" onClick={() => setOtherOpen(true)} />
            )}
          </Box>
          <Collapse in={otherOpen} unmountOnExit>
            <Box sx={{ mt: 1, display: "flex", gap: 0.75, alignItems: "center" }}>
              <Box
                component="input"
                type="text"
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleOtherSubmit();
                }}
                placeholder="Tell us what went wrong…"
                autoFocus
                sx={{
                  flex: 1,
                  minWidth: 0,
                  // ≥16px on touch so iOS Safari doesn't zoom on focus.
                  fontSize: { xs: 16, md: 12.5 },
                  fontFamily: "inherit",
                  color: "#1e293b",
                  backgroundColor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  px: 1.25,
                  py: 0.75,
                  outline: "none",
                  "&:focus": { borderColor: "#fca5a5", backgroundColor: "#fff" },
                  "&::placeholder": { color: "#94a3b8" },
                }}
              />
              <Box
                component="button"
                type="button"
                onClick={handleOtherSubmit}
                disabled={!otherText.trim()}
                sx={{
                  px: 1.5,
                  py: 0.75,
                  cursor: "pointer",
                  borderRadius: "8px",
                  border: "none",
                  backgroundColor: "#dc2626",
                  color: "#fff",
                  fontSize: 11.5,
                  fontWeight: 700,
                  flexShrink: 0,
                  "&:hover": { backgroundColor: "#b91c1c" },
                  "&:disabled": {
                    backgroundColor: "#e2e8f0",
                    color: "#94a3b8",
                    cursor: "not-allowed",
                  },
                }}
              >
                Send
              </Box>
            </Box>
          </Collapse>
        </Box>
      </Collapse>
    </Box>
  );
};

export default FeedbackBar;
